import { cn } from '@/utils/cn'
import { getMessage } from '@/utils/messages'

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg'
  label?: string
  className?: string
}

const SIZE_MAP = {
  sm: 'w-4 h-4 border-2',
  md: 'w-8 h-8 border-2',
  lg: 'w-12 h-12 border-[3px]',
}

/**
 * Simple CSS-animated loading spinner.
 * Uses CSS custom properties so it inherits the design token accent colour.
 */
export function LoadingSpinner({ size = 'md', label, className }: LoadingSpinnerProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3', className)}>
      <div
        className={cn(
          'rounded-full animate-spin',
          'border-[var(--surface3)] border-t-[var(--accent)]',
          SIZE_MAP[size]
        )}
        role="status"
        aria-label={label ?? getMessage('common.loading')}
      />
      {label && (
        <span className="text-[11px] text-[var(--text-muted)] font-mono tracking-wide">
          {label}
        </span>
      )}
    </div>
  )
}

/** Full-page loading overlay */
export function PageLoader({ label = getMessage('common.loading') }: { label?: string }) {
  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-white/20 backdrop-blur-[2px]">
      <LoadingSpinner size="lg" label={label} />
    </div>
  )
}
